import React, { FC } from "react";
import { connect } from "react-redux";

import { faClock } from "@fortawesome/pro-regular-svg-icons/faClock";
import { faCar } from "@fortawesome/pro-solid-svg-icons/faCar";
import { faDollarSign } from "@fortawesome/pro-solid-svg-icons/faDollarSign";
import { faLocationDot } from "@fortawesome/pro-solid-svg-icons/faLocationDot";
import { faParking } from "@fortawesome/pro-solid-svg-icons/faParking";
import { faRoad } from "@fortawesome/pro-solid-svg-icons/faRoad";
import partial from "lodash-es/partial";
import { defineMessages, useIntl } from "react-intl";

import localSettings from "@app/config/LocalSettings";
import { getTripsStats } from "@app/scene/reports/store/selectors";
import { areMileageTrackingAmountsVisible } from "@app/store/iam";
import { RootState } from "@app/store/rootReducer";
import { meterDistanceFormatter } from "@app/util/fieldModel/impl/fieldUtil";
import { formatDuration } from "@app/util/formatters";

import TripStats from "../../types/TripsStats";

import Card from "./Card";
import styles from "./Stats.module.scss";

const messages = defineMessages({
  driveTime: {
    id: "reports.mileageTracking.stats.driveTime",
    defaultMessage: "Drive Time",
    description: "Title of the drive time stats card on the Mileage Tracking page",
  },
  parkingFee: {
    id: "reports.mileageTracking.stats.parkingFee",
    defaultMessage: "Parking",
    description: "Title of the parking stats card on the Mileage Tracking page",
  },
  tollFee: {
    id: "reports.mileageTracking.stats.tollFee",
    defaultMessage: "Tolls",
    description: "Title of the tolls stats card on the Mileage Tracking page",
  },
  totalCount: {
    id: "reports.mileageTracking.stats.totalCount",
    defaultMessage: "Total Trips",
    description: "Title of the business cost stats card on the Mileage Tracking page",
  },
  totalDistance: {
    id: "reports.mileageTracking.stats.totalDistance",
    defaultMessage: "Total Distance",
    description: "Title of the total distance stats card on the Mileage Tracking page",
  },
  value: {
    id: "reports.mileageTracking.stats.value",
    defaultMessage: "Value",
    description: "Title of the business cost stats card on the Mileage Tracking page",
  },
});

export interface StatsProps {
  showAmounts: boolean;
  stats: TripStats;
}

const Stats: FC<StatsProps> = ({ showAmounts, stats }) => {
  const intl = useIntl();

  const moneyFormatter = (value: number) => {
    const currencyCode = localSettings.getCurrencyCode();
    if (!Number.isFinite(value)) {
      return "";
    }

    return intl.formatNumber(value, {
      currency: currencyCode,
      minimumFractionDigits: 0,
      style: "currency",
    });
  };

  const distanceFormatter = (value: number) => {
    return meterDistanceFormatter({}, value);
  };

  const durationFormatter = partial(formatDuration, intl);

  return (
    <div className={styles.container}>
      {showAmounts && (
        <>
          <Card
            icon={faDollarSign}
            title={intl.formatMessage(messages.value)}
            value={stats.value}
            valueFormatter={moneyFormatter}
          />
          <Card
            icon={faParking}
            title={intl.formatMessage(messages.parkingFee)}
            value={stats.parkingFee}
            valueFormatter={moneyFormatter}
          />
          <Card
            icon={faRoad}
            title={intl.formatMessage(messages.tollFee)}
            value={stats.tollFee}
            valueFormatter={moneyFormatter}
          />
        </>
      )}
      <Card
        icon={faLocationDot}
        title={intl.formatMessage(messages.totalDistance)}
        value={stats.totalDistance}
        valueFormatter={distanceFormatter}
      />
      <Card icon={faCar} title={intl.formatMessage(messages.totalCount)} value={stats.totalCount} />
      <Card
        icon={faClock}
        title={intl.formatMessage(messages.driveTime)}
        value={stats.totalDuration}
        valueFormatter={durationFormatter}
      />
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  showAmounts: areMileageTrackingAmountsVisible(state),
  stats: getTripsStats(state),
});

export default connect(mapStateToProps)(Stats);
